import { shape } from 'prop-types'
import { useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { Icon } from '../Icon'

export type SelectedPhoto = { type: 'file'; file: File } | { type: 'url'; url: string }

type Props = {
    onSelectPhoto: (photoFile: File) => void
    onClearPhoto: () => void
    selectedPhoto?: SelectedPhoto | null
    size?: 'small' | 'medium'
    shape?: 'circle' | 'card-reader-rectangle'
}

const ALLOWED_FILE_TYPES = ['image/png', 'image/jpg', 'image/jpeg']

export const PhotoPicker = ({
    onSelectPhoto,
    onClearPhoto,
    selectedPhoto,
    size = 'small',
    shape = 'circle',
}: Props) => {
    const photoFileInputRef = useRef<HTMLInputElement>(null)

    const [isDraggingOver, setIsDraggingOver] = useState(false)

    const handleDragEvent = (event: React.DragEvent) => {
        if (event.dataTransfer.types.length !== 1 || event.dataTransfer.types[0] !== 'Files') {
            return
        }

        event.preventDefault()
        event.stopPropagation()

        switch (event.type) {
            case 'dragover':
            case 'dragenter': {
                setIsDraggingOver(true)
                break
            }
            case 'dragleave': {
                setIsDraggingOver(false)
                break
            }
        }
    }

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault()
        event.stopPropagation()

        setIsDraggingOver(false)

        const firstFile = Array.from(event.dataTransfer.files)[0]
        if (firstFile === undefined || !ALLOWED_FILE_TYPES.includes(firstFile.type)) {
            return
        }

        onSelectPhoto(firstFile)
    }

    const handleOpenFilePicker = () => {
        photoFileInputRef.current?.click()
    }

    const handlePhotoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const firstFile = Array.from(event.target.files ?? [])[0]
        if (firstFile === undefined) {
            return
        }
        onSelectPhoto(firstFile)
    }

    const handleClickRemovePhoto = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!selectedPhoto) {
            return
        }

        event.stopPropagation()
        event.preventDefault()
        onClearPhoto()
    }

    return (
        <div className="flex flex-col items-center justify-center gap-y-4">
            <div
                className={twMerge(
                    'relative flex cursor-pointer items-center justify-center bg-gray-100',
                    size === 'small' ? 'size-32' : 'size-[200px]',
                    shape === 'circle' ? 'rounded-full' : 'aspect-[9/16]'
                )}
                onDragOver={handleDragEvent}
                onDragEnter={handleDragEvent}
                onDragLeave={handleDragEvent}
                onDrop={handleDrop}
                onClick={handleOpenFilePicker}
            >
                {isDraggingOver ? (
                    <div className="text-gray-600">Drop photo</div>
                ) : selectedPhoto === undefined || selectedPhoto === null ? (
                    <Icon icon="image.large" className="size-8 text-gray-600" />
                ) : selectedPhoto.type === 'file' ? (
                    <img
                        src={URL.createObjectURL(selectedPhoto.file)}
                        className={twMerge(
                            'h-full w-full object-cover',
                            shape === 'circle' ? 'rounded-full' : 'aspect-[9/16]'
                        )}
                    />
                ) : (
                    <img
                        src={selectedPhoto.url}
                        className={twMerge(
                            'h-full w-full object-cover',
                            shape === 'circle' ? 'rounded-full' : 'aspect-[9/16]'
                        )}
                    />
                )}
                <button
                    onClick={handleClickRemovePhoto}
                    className={twMerge(
                        'absolute right-0 bottom-0 z-10 flex size-9 items-center justify-center rounded-full border border-gray-300 bg-white shadow-md shadow-black/20',
                        'hover:bg-gray-100',
                        size === 'small' ? 'size-9' : 'size-12'
                    )}
                >
                    <Icon icon={!!selectedPhoto ? 'trash.small' : 'camera.small'} />
                </button>
            </div>

            <input
                type="file"
                className="hidden"
                onChange={handlePhotoFileChange}
                ref={photoFileInputRef}
                accept={ALLOWED_FILE_TYPES.join(',')}
            />
        </div>
    )
}
